import { ActionIcon, Button, Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { IconArchive, IconArrowBackUp, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import {
  useSavePosition,
  useServePositions,
  useServeTeams,
  useSetPositionActive,
} from "./services-queries";

export interface PositionsModalProps {
  readonly opened: boolean;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

/** Positions on each serve team (ADR 0043 §1): the grid's rows. Teams themselves live in Groups. */
export function PositionsModal({ opened, canManage, onClose }: PositionsModalProps) {
  const teams = useServeTeams();
  const positions = useServePositions();
  const save = useSavePosition();
  const setActive = useSetPositionActive();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "That team already has a position with this name."
      : save.isError
        ? "The position was not saved."
        : null;
  return (
    <Modal opened={opened} onClose={onClose} title="Positions" radius="lg" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          A position is a role on a serve team: Greeter, Sound, Vocals. Teams and who is on them are
          managed in Groups.
        </Text>
        {(teams.data ?? []).map((team) => {
          const rows = (positions.data ?? []).filter((p) => p.teamId === team.id);
          return (
            <Stack key={team.id} gap={4}>
              <Text size="sm" fw={600}>
                {team.name}
              </Text>
              {rows.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No positions yet.
                </Text>
              ) : (
                rows.map((p) => (
                  <Group key={p.id} justify="space-between" wrap="nowrap">
                    <Text size="sm" {...(p.isActive ? {} : { c: "dimmed" })}>
                      {p.name}
                      {p.isActive ? "" : " (archived)"}
                    </Text>
                    {canManage ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label={`${p.isActive ? "Archive" : "Restore"} ${p.name} on ${team.name}`}
                        loading={setActive.isPending}
                        onClick={() => {
                          setActive.mutate({ positionId: p.id, isActive: !p.isActive });
                        }}
                      >
                        {p.isActive ? <IconArchive size={16} /> : <IconArrowBackUp size={16} />}
                      </ActionIcon>
                    ) : null}
                  </Group>
                ))
              )}
            </Stack>
          );
        })}
        {canManage ? (
          <Group align="flex-end" wrap="nowrap">
            <Select
              label="Team"
              placeholder="Choose a team"
              data={(teams.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
              value={teamId}
              onChange={setTeamId}
              w={200}
            />
            <TextInput
              label="New position"
              placeholder="Greeter"
              value={name}
              onChange={(e) => {
                setName(e.currentTarget.value);
              }}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              disabled={teamId === null || name.trim() === ""}
              loading={save.isPending}
              onClick={() => {
                if (teamId === null) return;
                const sortOrder = (positions.data ?? []).filter((p) => p.teamId === teamId).length;
                save.mutate(
                  { positionId: null, teamId, name, sortOrder },
                  {
                    onSuccess: () => {
                      setName("");
                    },
                  },
                );
              }}
            >
              Add
            </Button>
          </Group>
        ) : null}
        {errorText !== null ? (
          <Text size="sm" c="red">
            {errorText}
          </Text>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
